import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { Bell, AlertTriangle, Clock, UserX, TrendingDown, Filter } from 'lucide-react';

export default function NotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, high, medium, low
  const [typeFilter, setTypeFilter] = useState('all');
  
  useEffect(() => {
    loadNotifications();
  }, []);
  
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/staff/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Ошибка загрузки уведомлений:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredNotifications = notifications.filter(n => {
    if (filter !== 'all' && n.priority !== filter) return false;
    if (typeFilter !== 'all' && n.type !== typeFilter) return false;
    return true;
  });
  
  const getIcon = (type) => {
    switch (type) {
      case 'payment-overdue':
      case 'no-payment':
        return <AlertTriangle className="text-red-500" size={24} />;
      case 'payment-soon':
        return <Clock className="text-yellow-500" size={24} />;
      case 'practice-overdue':
        return <AlertTriangle className="text-red-500" size={24} />;
      case 'practice-inactive':
        return <Clock className="text-yellow-500" size={24} />;
      case 'lessons-ending':
        return <TrendingDown className="text-orange-500" size={24} />;
      case 'no-instructor':
        return <UserX className="text-blue-500" size={24} />;
      default:
        return <Bell className="text-gray-500" size={24} />;
    }
  };
  
  const getBgColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 border-red-200 hover:bg-red-100';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100';
      case 'low':
        return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      default:
        return 'bg-gray-50 border-gray-200 hover:bg-gray-100';
    }
  };
  
  const getTypeLabel = (type) => {
    switch (type) {
      case 'payment-overdue': return 'Просрочена оплата';
      case 'no-payment': return 'Нет оплат';
      case 'payment-soon': return 'Скоро оплата';
      case 'practice-overdue': return 'Давно не занимался';
      case 'practice-inactive': return 'Неактивен';
      case 'lessons-ending': return 'Заканчиваются занятия';
      case 'no-instructor': return 'Нет инструктора';
      default: return 'Уведомление';
    }
  };
  
  const handleStudentClick = (studentId) => {
    navigate(`/editor-user?studentId=${studentId}`);
  };
  
  if (user?.status !== 'staff') {
    return <div className="text-red-500 text-center mt-20 text-2xl">Доступ запрещён</div>;
  }
  
  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] pb-20">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 pt-24">
        <div className="mb-6 flex items-center gap-3">
          <Bell className="text-[#b8975a]" size={32} />
          <div>
            <h1 className="text-2xl font-bold text-[#dedede]">Уведомления</h1>
            <p className="text-sm text-gray-400">Система напоминаний о учениках</p>
          </div>
        </div>
        
        {/* Статистика */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#252525] rounded-lg p-4 border border-white/10">
            <div className="text-3xl font-bold text-[#b8975a]">{stats.total}</div>
            <div className="text-sm text-gray-400 mt-1">Всего</div>
          </div>
          <div className="bg-red-900/20 rounded-lg p-4 border border-red-500/30">
            <div className="text-3xl font-bold text-red-400">{stats.high}</div>
            <div className="text-sm text-gray-400 mt-1">Срочные</div>
          </div>
          <div className="bg-yellow-900/20 rounded-lg p-4 border border-yellow-500/30">
            <div className="text-3xl font-bold text-yellow-400">{stats.medium}</div>
            <div className="text-sm text-gray-400 mt-1">Важные</div>
          </div>
          <div className="bg-blue-900/20 rounded-lg p-4 border border-blue-500/30">
            <div className="text-3xl font-bold text-blue-400">{stats.low}</div>
            <div className="text-sm text-gray-400 mt-1">Информация</div>
          </div>
        </div>
        
        {/* Фильтры */}
        <div className="bg-[#252525] rounded-lg border border-white/10 p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={18} className="text-[#b8975a]" />
            <span className="text-sm font-medium text-slate-300">Фильтры</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                Приоритет
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none"
              >
                <option value="all" className="bg-[#1b1b1b]">Все</option>
                <option value="high" className="bg-[#1b1b1b]">🔴 Срочные</option>
                <option value="medium" className="bg-[#1b1b1b]">🟡 Важные</option>
                <option value="low" className="bg-[#1b1b1b]">🔵 Информация</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                Тип
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#b8975a] focus:outline-none"
              >
                <option value="all" className="bg-[#1b1b1b]">Все типы</option>
                <option value="payment-overdue" className="bg-[#1b1b1b]">Просрочена оплата</option>
                <option value="no-payment" className="bg-[#1b1b1b]">Нет оплат</option>
                <option value="payment-soon" className="bg-[#1b1b1b]">Скоро оплата</option>
                <option value="practice-overdue" className="bg-[#1b1b1b]">Давно не занимался</option>
                <option value="practice-inactive" className="bg-[#1b1b1b]">Неактивен</option>
                <option value="lessons-ending" className="bg-[#1b1b1b]">Заканчиваются занятия</option>
                <option value="no-instructor" className="bg-[#1b1b1b]">Нет инструктора</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={loadNotifications}
            className="mt-4 px-4 py-2 bg-[#b8975a] text-black rounded-lg text-sm font-medium hover:bg-[#a07d4a] transition"
          >
            🔄 Обновить
          </button>
        </div>
        
        {/* Список уведомлений */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#b8975a] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Загрузка уведомлений...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12 bg-[#252525] rounded-lg border border-white/10">
            <div className="text-6xl mb-4">✅</div>
            <p className="text-xl text-gray-400">Нет уведомлений</p>
            <p className="text-sm text-gray-500 mt-2">Все ученики в порядке!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((n, i) => (
              <div
                key={i}
                onClick={() => handleStudentClick(n.studentId)}
                className={`p-4 rounded-lg border cursor-pointer transition ${getBgColor(n.priority)}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(n.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg font-bold text-gray-900 truncate">
                        {n.studentName}
                      </span>
                      {n.group && (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full flex-shrink-0">
                          Группа {n.group}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-700 mb-2">{n.message}</p>
                    
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <span className="font-medium">{getTypeLabel(n.type)}</span>
                      </span>
                      
                      {n.phone && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span>{n.phone}</span>
                        </>
                      )}
                      
                      {n.days !== null && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span>{n.days} дней</span>
                        </>
                      )}
                      
                      {n.lessonsLeft !== undefined && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span>Осталось {n.lessonsLeft} занятий</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <span className="text-xs text-gray-500">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <BottomNav />
    </div>
  );
}